import React, { useEffect, useMemo, useRef, useState } from 'react';
import RawDsSelect from '../../common/DsSelect';
import { formatDateOnlyJST, formatNumberJP } from '../../../utils/datetime';
import type { EventMarketingLog } from '../../../api/eventMarketing';
import useResponsiveChartWidth from '../../../hooks/useResponsiveChartWidth';

export type TicketSalesMarketingTimeline = {
  windowDays: number;
  channelLimit: number;
  points: Array<{
    date: string;
    soldTotal: number;
    channels: Array<{ channelName: string; soldCount: number }>;
  }>;
};
type ChartPoint = {
  date: string;
  daily: number;
  cumulative: number;
  eplusDaily: number;
  teketDaily: number;
  eplusCumulative: number;
  teketCumulative: number;
  logSummaries: string[];
};
type ChartMarker = { date: string; number: number; count: number; summaries: string[] };
type ChartData = { points: ChartPoint[]; markers: ChartMarker[]; total: number };
type TooltipTarget = { kind: 'sales' | 'marker'; date: string };
type SelectProps = { value?: string; onChange: (event: { target: { value: string } }) => void; children: React.ReactNode; 'aria-label'?: string };
const DsSelect = RawDsSelect as unknown as React.ComponentType<SelectProps>;

const ALL_CHANNELS = { value: '', label: '全販路' };
const dateKey = (value: unknown) => {
  const raw = String(value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return formatDateOnlyJST(raw, '').replaceAll('/', '-');
};
const numeric = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatMonthDay = (value: string) => {
  const [, month = '', day = ''] = dateKey(value).split('-');
  return `${Number(month)}/${Number(day)}`;
};

export function calculateNiceScale(values: number[], tickCount = 4) {
  const maxValue = Math.max(1, ...values.filter(Number.isFinite));
  const roughStep = maxValue / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const niceFactor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = niceFactor * magnitude;
  const max = step * Math.ceil(maxValue / step);
  return { max, ticks: Array.from({ length: Math.round(max / step) + 1 }, (_, index) => index * step) };
}

export function pickDateTickIndexes(length: number, target = 6) {
  if (length <= 1) return length ? [0] : [];
  const count = Math.min(target, length);
  return [...new Set(Array.from({ length: count }, (_, index) => Math.round((index / (count - 1)) * (length - 1))))];
}

export function buildSalesCorrelationData(
  timeline: TicketSalesMarketingTimeline | null | undefined,
  logs: EventMarketingLog[] = [],
  channel = '',
): ChartData {
  const salesByDate = new Map<string, number>();
  const channelSalesByDate = new Map<string, { eplus: number; teket: number }>();
  (timeline?.points || []).forEach((point) => {
    const date = dateKey(point.date);
    if (!date) return;
    const channels = Array.isArray(point.channels) ? point.channels : [];
    const selectedSold = channel
      ? channels.filter((item) => item.channelName === channel).reduce((sum, item) => sum + numeric(item.soldCount), 0)
      : numeric(point.soldTotal);
    salesByDate.set(date, selectedSold);
    channelSalesByDate.set(date, {
      eplus: channels.filter((item) => item.channelName === 'イープラス').reduce((sum, item) => sum + numeric(item.soldCount), 0),
      teket: channels.filter((item) => item.channelName === 'Teket').reduce((sum, item) => sum + numeric(item.soldCount), 0),
    });
  });
  const logsByDate = new Map<string, string[]>();
  logs.forEach((log) => {
    const date = dateKey(log.occurredAt);
    if (!date) return;
    const summaries = logsByDate.get(date) || [];
    summaries.push(String(log.summary || '実施内容未設定'));
    logsByDate.set(date, summaries);
  });
  const dates = [...new Set([...salesByDate.keys(), ...channelSalesByDate.keys(), ...logsByDate.keys()])].sort();
  let cumulative = 0;
  let eplusCumulative = 0;
  let teketCumulative = 0;
  const points = dates.map((date) => {
    const daily = salesByDate.get(date) || 0;
    cumulative += daily;
    const channelSales = channelSalesByDate.get(date) || { eplus: 0, teket: 0 };
    eplusCumulative += channelSales.eplus;
    teketCumulative += channelSales.teket;
    return {
      date,
      daily,
      cumulative,
      eplusDaily: channelSales.eplus,
      teketDaily: channelSales.teket,
      eplusCumulative,
      teketCumulative,
      logSummaries: logsByDate.get(date) || [],
    };
  });
  const visibleDates = new Set(dates);
  const markers = [...logsByDate.entries()]
    .filter(([date]) => visibleDates.has(date))
    .sort(([left], [right]) => left.localeCompare(right))
    .slice(-8)
    .map(([date, summaries], index) => ({ date, number: index + 1, count: summaries.length, summaries }));
  return { points, markers, total: cumulative };
}

export default function EventMarketingSalesCorrelationChart({
  timeline,
  logs,
  loading = false,
}: {
  timeline: TicketSalesMarketingTimeline | null;
  logs: EventMarketingLog[];
  loading?: boolean;
}) {
  const [channel, setChannel] = useState('');
  const [allChannelMode, setAllChannelMode] = useState<'total' | 'channels'>('total');
  const [hoveredTooltip, setHoveredTooltip] = useState<TooltipTarget | null>(null);
  const [pinnedTooltip, setPinnedTooltip] = useState<TooltipTarget | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const chartInteractionRef = useRef<HTMLDivElement>(null);
  const width = useResponsiveChartWidth(chartInteractionRef, 760);
  const data = useMemo(() => buildSalesCorrelationData(timeline, logs, channel), [channel, logs, timeline]);
  const channelTotals = useMemo(() => {
    const totals = new Map<string, number>();
    (timeline?.points || []).forEach((point) => (point.channels || []).forEach((item) => {
      const name = String(item.channelName || '').trim();
      if (name) totals.set(name, (totals.get(name) || 0) + numeric(item.soldCount));
    }));
    return [...totals.entries()]
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], 'ja'))
      .map(([value, total]) => ({ value, label: value, total }));
  }, [timeline]);
  const channelOptions = [ALL_CHANNELS, ...channelTotals];
  const supportsLegacyChannelComparison = channelTotals.some((item) => item.value === 'イープラス' || item.value === 'Teket');
  const height = 250;
  const left = 44;
  const right = 16;
  const top = 24;
  const bottom = 34;
  const plotWidth = width - left - right;
  const baseline = height - bottom;
  const separateChannels = !channel && allChannelMode === 'channels' && supportsLegacyChannelComparison;
  const scaleValues = separateChannels
    ? data.points.flatMap((point) => [point.eplusCumulative, point.teketCumulative, point.eplusDaily + point.teketDaily])
    : data.points.flatMap((point) => [point.cumulative, point.daily]);
  const yScale = calculateNiceScale(scaleValues);
  const xAt = (index: number) => left + (data.points.length <= 1 ? plotWidth / 2 : (index / (data.points.length - 1)) * plotWidth);
  const lineY = (value: number) => baseline - (value / yScale.max) * (baseline - top);
  const barHeight = (value: number) => (value / yScale.max) * (baseline - top);
  const displayedCumulative = (point: ChartPoint) => separateChannels ? Math.max(point.eplusCumulative, point.teketCumulative) : point.cumulative;
  const pointByDate = new Map(data.points.map((point, index) => [point.date, { point, index }]));
  const polyline = data.points.map((point, index) => `${xAt(index)},${lineY(point.cumulative)}`).join(' ');
  const eplusPolyline = data.points.map((point, index) => `${xAt(index)},${lineY(point.eplusCumulative)}`).join(' ');
  const teketPolyline = data.points.map((point, index) => `${xAt(index)},${lineY(point.teketCumulative)}`).join(' ');
  const dateTickIndexes = pickDateTickIndexes(data.points.length);
  const fallbackPoint = data.points[data.points.length - 1];
  const activeTooltip = pinnedTooltip || hoveredTooltip;
  const detailDate = activeTooltip?.date || (selectedDate && pointByDate.has(selectedDate) ? selectedDate : null) || fallbackPoint?.date;
  const activeDate = activeTooltip?.kind === 'sales' ? activeTooltip.date : null;
  const activeEntry = activeDate ? pointByDate.get(activeDate) : undefined;
  const activePoint = activeEntry?.point;
  const detailPoint = detailDate ? pointByDate.get(detailDate)?.point : undefined;
  const hitWidth = plotWidth / Math.max(data.points.length, 1);
  const activeX = activeEntry ? xAt(activeEntry.index) : left;
  const tooltipWidth = 250;
  const tooltipX = Math.min(width - right - tooltipWidth, Math.max(left, activeX - tooltipWidth / 2));
  const selectedChannelLabel = channel || '全販路';
  const markerByDate = new Map(data.markers.map((marker) => [marker.date, marker]));
  const activeMarkerDate = activeTooltip?.kind === 'marker' ? activeTooltip.date : null;
  const activeMarker = activeMarkerDate ? markerByDate.get(activeMarkerDate) : undefined;
  const activeMarkerEntry = activeMarker ? pointByDate.get(activeMarker.date) : undefined;
  const markerTooltipWidth = 220;
  const markerTooltipHeight = 46;
  const markerTooltipX = activeMarkerEntry ? Math.min(width - right - markerTooltipWidth, Math.max(left, xAt(activeMarkerEntry.index) - markerTooltipWidth / 2)) : left;
  const activeMarkerY = activeMarkerEntry ? Math.max(top + 10, lineY(displayedCumulative(activeMarkerEntry.point)) - 16) : top;
  const markerTooltipY = Math.min(baseline - markerTooltipHeight, Math.max(top, activeMarkerY + 14));
  const markerSummary = activeMarker ? activeMarker.summaries.join('／') : '';
  const compactMarkerSummary = markerSummary.length > 32 ? `${markerSummary.slice(0, 31)}…` : markerSummary;

  const closeTooltips = () => {
    setHoveredTooltip(null);
    setPinnedTooltip(null);
  };
  const togglePinnedTooltip = (target: TooltipTarget) => {
    setSelectedDate(target.date);
    setHoveredTooltip(null);
    setPinnedTooltip((current) => current?.kind === target.kind && current.date === target.date ? null : target);
  };

  useEffect(() => {
    const clearTooltips = () => {
      setHoveredTooltip(null);
      setPinnedTooltip(null);
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (!chartInteractionRef.current?.contains(event.target as Node)) clearTooltips();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearTooltips();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <section className="event-marketing-sales-correlation" aria-labelledby="event-marketing-sales-correlation-title">
      <div className="event-marketing-sales-correlation__head">
        <div>
          <h2 id="event-marketing-sales-correlation-title">券売推移 × 施策</h2>
          <p>販売の動きと施策を同じ時間軸で確認できます。</p>
        </div>
        <div className="event-marketing-sales-correlation__controls">
          {channel === '' && supportsLegacyChannelComparison ? (
            <div className="segmented event-marketing-sales-correlation__mode" aria-label="全販路の表示方法">
              <button type="button" className={allChannelMode === 'total' ? 'active' : ''} aria-pressed={allChannelMode === 'total'} onClick={() => { closeTooltips(); setAllChannelMode('total'); }}>合計</button>
              <button type="button" className={allChannelMode === 'channels' ? 'active' : ''} aria-pressed={allChannelMode === 'channels'} onClick={() => { closeTooltips(); setAllChannelMode('channels'); }}>販路別</button>
            </div>
          ) : null}
          <DsSelect value={channel} aria-label="券売サイトで絞り込む" onChange={(event) => { closeTooltips(); setChannel(event.target.value); }}>
            {channelOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </DsSelect>
        </div>
      </div>
      {loading ? <p className="event-marketing-sales-correlation__empty">券売状況を読み込んでいます…</p> : null}
      {!loading && !data.points.length ? <p className="event-marketing-sales-correlation__empty">表示できる券売状況はまだありません。</p> : null}
      {!loading && data.points.length ? (
        <>
          <div ref={chartInteractionRef} className="event-marketing-sales-correlation__chart">
            <svg
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label={`${selectedChannelLabel}${separateChannels ? 'を販路別表示した' : 'の'}累計販売数と日別販売数。累計${formatNumberJP(data.total)}枚。縦軸最大${formatNumberJP(yScale.max)}枚`}
              onMouseLeave={() => setHoveredTooltip(null)}
            >
              <line x1={left} y1={baseline} x2={width - right} y2={baseline} className="event-marketing-sales-correlation__axis" />
              {yScale.ticks.map((tick) => (
                <g key={tick}>
                  <line x1={left} y1={lineY(tick)} x2={width - right} y2={lineY(tick)} className="event-marketing-sales-correlation__grid" />
                  <text x={left - 6} y={lineY(tick) + 4} textAnchor="end" className="event-marketing-sales-correlation__axis-label">{formatNumberJP(tick)}枚</text>
                </g>
              ))}
              {data.points.map((point, index) => {
                const barWidth = Math.min(18, Math.max(5, plotWidth / Math.max(data.points.length, 1) * 0.45));
                if (separateChannels) {
                  const eplusHeight = barHeight(point.eplusDaily);
                  const teketHeight = barHeight(point.teketDaily);
                  return <g key={point.date}><rect x={xAt(index) - barWidth / 2} y={baseline - eplusHeight} width={barWidth} height={eplusHeight} rx="2" className="event-marketing-sales-correlation__bar is-eplus" /><rect x={xAt(index) - barWidth / 2} y={baseline - eplusHeight - teketHeight} width={barWidth} height={teketHeight} rx="2" className="event-marketing-sales-correlation__bar is-teket" /></g>;
                }
                const valueHeight = barHeight(point.daily);
                return <rect key={point.date} x={xAt(index) - barWidth / 2} y={baseline - valueHeight} width={barWidth} height={valueHeight} rx="2" className="event-marketing-sales-correlation__bar" />;
              })}
              {separateChannels ? <><polyline data-series="イープラス" points={eplusPolyline} className="event-marketing-sales-correlation__line is-eplus" /><polyline data-series="Teket" points={teketPolyline} className="event-marketing-sales-correlation__line is-teket" /></> : <polyline data-series={selectedChannelLabel} points={polyline} className="event-marketing-sales-correlation__line" />}
              {activePoint && !activeMarker ? (
                <g className="event-marketing-sales-correlation__selection">
                  <line aria-hidden="true" x1={activeX} y1={top} x2={activeX} y2={baseline} className="event-marketing-sales-correlation__selection-line" />
                  {separateChannels ? <><circle aria-hidden="true" cx={activeX} cy={lineY(activePoint.eplusCumulative)} r="5" className="event-marketing-sales-correlation__selection-point is-eplus" /><circle aria-hidden="true" cx={activeX} cy={lineY(activePoint.teketCumulative)} r="5" className="event-marketing-sales-correlation__selection-point is-teket" /></> : <circle aria-hidden="true" cx={activeX} cy={lineY(activePoint.cumulative)} r="5" className="event-marketing-sales-correlation__selection-point" />}
                  <g id={`sales-tooltip-${activePoint.date}`} role="tooltip" transform={`translate(${tooltipX} 6)`}>
                    <rect width={tooltipWidth} height="66" rx="6" className="event-marketing-sales-correlation__tooltip-bg" />
                    <text x="10" y="17" className="event-marketing-sales-correlation__tooltip-title">{formatMonthDay(activePoint.date)}</text>
                    <text x="10" y="35" className="event-marketing-sales-correlation__tooltip-text">{selectedChannelLabel} 当日 {formatNumberJP(activePoint.daily)}枚・累計 {formatNumberJP(activePoint.cumulative)}枚</text>
                    <text x="10" y="53" className="event-marketing-sales-correlation__tooltip-text">イープラス {formatNumberJP(activePoint.eplusDaily)}枚 ／ Teket {formatNumberJP(activePoint.teketDaily)}枚{activePoint.logSummaries.length ? ` ／ 施策 ${activePoint.logSummaries.length}件` : ''}</text>
                  </g>
                </g>
              ) : null}
              {data.points.map((point, index) => (
                <rect
                  key={`hit-${point.date}`}
                  x={Math.max(left, xAt(index) - hitWidth / 2)}
                  y={top}
                  width={Math.min(hitWidth, width - right - Math.max(left, xAt(index) - hitWidth / 2))}
                  height={baseline - top}
                  className="event-marketing-sales-correlation__hit-area"
                  role="button"
                  tabIndex={0}
                  aria-pressed={pinnedTooltip?.kind === 'sales' && pinnedTooltip.date === point.date}
                  aria-describedby={activeTooltip?.kind === 'sales' && activeTooltip.date === point.date ? `sales-tooltip-${point.date}` : undefined}
                  aria-label={`${formatMonthDay(point.date)}、${selectedChannelLabel}の当日販売${formatNumberJP(point.daily)}枚、累計${formatNumberJP(point.cumulative)}枚`}
                  onMouseEnter={() => { if (!pinnedTooltip) setHoveredTooltip({ kind: 'sales', date: point.date }); }}
                  onMouseLeave={() => setHoveredTooltip((current) => current?.kind === 'sales' && current.date === point.date ? null : current)}
                  onFocus={() => { if (!pinnedTooltip) setHoveredTooltip({ kind: 'sales', date: point.date }); }}
                  onBlur={() => setHoveredTooltip((current) => current?.kind === 'sales' && current.date === point.date ? null : current)}
                  onClick={() => togglePinnedTooltip({ kind: 'sales', date: point.date })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      togglePinnedTooltip({ kind: 'sales', date: point.date });
                    }
                  }}
                />
              ))}
              {data.markers.map((marker) => {
                const found = pointByDate.get(marker.date);
                if (!found) return null;
                const cx = xAt(found.index);
                const markerY = lineY(displayedCumulative(found.point));
                const cy = Math.max(top + 10, markerY - 16);
                return (
                  <g
                    key={marker.date}
                    className="event-marketing-sales-correlation__marker-action"
                    role="button"
                    tabIndex={0}
                    aria-pressed={pinnedTooltip?.kind === 'marker' && pinnedTooltip.date === marker.date}
                    aria-describedby={activeTooltip?.kind === 'marker' && activeTooltip.date === marker.date ? `marker-tooltip-${marker.date}` : undefined}
                    aria-label={`${formatMonthDay(marker.date)}の施策 ${marker.count}件、${marker.summaries.join('／')}`}
                    onMouseEnter={() => { if (!pinnedTooltip) setHoveredTooltip({ kind: 'marker', date: marker.date }); }}
                    onMouseLeave={() => setHoveredTooltip((current) => current?.kind === 'marker' && current.date === marker.date ? null : current)}
                    onFocus={() => { if (!pinnedTooltip) setHoveredTooltip({ kind: 'marker', date: marker.date }); }}
                    onBlur={() => setHoveredTooltip((current) => current?.kind === 'marker' && current.date === marker.date ? null : current)}
                    onClick={() => togglePinnedTooltip({ kind: 'marker', date: marker.date })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        togglePinnedTooltip({ kind: 'marker', date: marker.date });
                      }
                    }}
                  >
                    <line x1={cx} y1={cy + 10} x2={cx} y2={markerY} className="event-marketing-sales-correlation__marker-line" />
                    <circle cx={cx} cy={cy} r="10" className="event-marketing-sales-correlation__marker" />
                    <text x={cx} y={cy + 4} textAnchor="middle" className="event-marketing-sales-correlation__marker-text">{marker.number}</text>
                  </g>
                );
              })}
              {activeMarker ? (
                <g id={`marker-tooltip-${activeMarker.date}`} className="event-marketing-sales-correlation__marker-tooltip" role="tooltip" aria-live="polite" transform={`translate(${markerTooltipX} ${markerTooltipY})`}>
                  <rect width={markerTooltipWidth} height={markerTooltipHeight} rx="6" className="event-marketing-sales-correlation__marker-tooltip-bg" />
                  <text x="10" y="17" className="event-marketing-sales-correlation__marker-tooltip-title">{formatMonthDay(activeMarker.date)}・施策 {activeMarker.count}件</text>
                  <text x="10" y="35" className="event-marketing-sales-correlation__marker-tooltip-text">{compactMarkerSummary}</text>
                </g>
              ) : null}
              {dateTickIndexes.map((index) => <text key={data.points[index].date} x={xAt(index)} y={height - 10} textAnchor={index === 0 ? 'start' : index === data.points.length - 1 ? 'end' : 'middle'} className="event-marketing-sales-correlation__date">{formatMonthDay(data.points[index].date)}</text>)}
            </svg>
          </div>
          <div className="event-marketing-sales-correlation__legend" aria-label="グラフの凡例">
            {separateChannels ? <><span><i className="is-line is-eplus" />イープラス累計</span><span><i className="is-line is-teket" />Teket累計</span><span><i className="is-bar" />日別販売</span></> : <><span><i className="is-line" />累計販売</span><span><i className="is-bar" />日別販売</span></>}<strong>累計 {formatNumberJP(data.total)}枚</strong>
          </div>
          {detailPoint ? (
            <div className="event-marketing-sales-correlation__active-detail" aria-live="polite" data-testid="sales-correlation-active-detail">
              <strong>{formatMonthDay(detailPoint.date)}</strong>
              <dl>
                <div><dt>{selectedChannelLabel} 当日</dt><dd>{formatNumberJP(detailPoint.daily)}枚</dd></div>
                <div><dt>{selectedChannelLabel} 累計</dt><dd>{formatNumberJP(detailPoint.cumulative)}枚</dd></div>
                <div><dt>イープラス 当日</dt><dd>{formatNumberJP(detailPoint.eplusDaily)}枚</dd></div>
                <div><dt>Teket 当日</dt><dd>{formatNumberJP(detailPoint.teketDaily)}枚</dd></div>
              </dl>
              <div className="event-marketing-sales-correlation__active-logs">
                <span>同日の施策 {detailPoint.logSummaries.length}件</span>
                <small>{detailPoint.logSummaries.length ? detailPoint.logSummaries.join('／') : '実施記録はありません'}</small>
              </div>
            </div>
          ) : null}
          <ul className="event-marketing-sales-correlation__channels" aria-label="販路別販売数">
            {channelTotals.map((item) => (
              <li key={item.value}><span>{item.label}</span><strong>{formatNumberJP(item.total)}枚</strong></li>
            ))}
          </ul>
          {data.markers.length ? (
            <ol className="event-marketing-sales-correlation__logs">
              {data.markers.map((marker) => (
                <li key={marker.date}>
                  <span>{marker.number}</span>
                  <div><strong>{formatMonthDay(marker.date)}{marker.count > 1 ? `・${marker.count}件` : ''}</strong><small>{marker.summaries.join('／')}</small></div>
                </li>
              ))}
            </ol>
          ) : <p className="event-marketing-sales-correlation__no-logs">この期間の実施記録はありません。</p>}
        </>
      ) : null}
      <p className="event-marketing-sales-correlation__note">施策と販売の相関を観察する表示であり、因果関係を示すものではありません。</p>
    </section>
  );
}
